<!--
╔════════════════════════════════════════════════════════════════════╗
║  SectionLeftTitleHtml.vue - Titolo a Sinistra con Contenuto       ║
╚════════════════════════════════════════════════════════════════════╝

DESCRIZIONE:
Componente per layout con eyebrow/titolo a sinistra e contenuto a
destra (testo HTML + immagine hero o layout portrait). Supporta due
modalità: immagine centrata o immagine portrait flottante a destra.

FEATURES:
  ✓ Layout 2 colonne (titolo | contenuto)
  ✓ Modalità portrait: immagine flottante con testo a sinistra
  ✓ Modalità normale: immagine hero centrata sopra testo
  ✓ Allegati files con icone
  ✓ Link esterni separati
  ✓ HTML content sanitizzato
  ✓ Lazy loading immagini

LAYOUT:
  Portrait (property portrait=1):
    [Titolo]           [Testo | Immagine Piccola]
    
  Normale (portrait=0 default):
    [Titolo]           [Immagine Hero Centrata]
                       [Testo HTML]
                       [Allegati/Link]

DIPENDENZE:
  - sanitizeHtmlRegex
-->

<template>
  <section class="section-left-title">
    <div class="container">
      <div class="content">
        
        <div class="left section-header">
          <p v-if="content.eyebrow" class="eyebrow">{{ content.eyebrow }}</p>
          <h2 class="title">{{ content.title }}</h2>
        </div>

        <div class="right">
          
          <div v-if="isPortrait && hasImage" class="content-with-portrait">
            <div class="portrait-image">
              <img :src="content.image" :alt="content.title || 'Ritratto'" loading="lazy" />
            </div>
            <div
              class="html-content"
              v-html="sanitizeHtml(content.text)"
            ></div>
          </div>

          <template v-else>
            
            <div v-if="hasImage" class="hero-image">
              <img :src="content.image" :alt="content.title || 'Immagine'" loading="lazy" />
            </div>

            <div
              class="html-content"
              v-html="sanitizeHtml(content.text)"
            ></div>
          </template>

          <div v-if="content.files?.length" class="attachments">
            <h4 class="attachments-title">Documenti allegati</h4>
            <ul class="attachments-list">
              <li
                v-for="file in content.files"
                :key="'file-' + file.fileUrl"
                class="attachment-row"
              >
                <a :href="file.fileUrl" target="_blank" rel="noopener">
                  <span class="attachment-icon">
                    <img :src="file.icon || pdfFallback" alt="PDF" />
                  </span>
                  <span class="attachment-label">{{ file.label }}</span>
                  <span v-if="file.size" class="file-size">({{ file.size }})</span>
                </a>
              </li>
            </ul>
          </div>

          <div v-if="content.links?.length" class="attachments">
            <h4 class="attachments-title">Collegamenti esterni</h4>
            <ul class="attachments-list">
              <li
                v-for="link in content.links"
                :key="'link-' + link.url"
                class="attachment-row"
              >
                <a :href="link.url" target="_blank" rel="noopener">
                  <span class="attachment-icon">
                    <img :src="link.icon || linkFallback" alt="Link" />
                  </span>
                  <span class="attachment-label">{{ link.titolo }}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { sanitizeHtmlRegex } from '@/utils/sanitizeHtml'

// ──────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ──────────────────────────────────────────────────────────────
const props = defineProps({
  /**
   * Contenuto della sezione
   * @type {Object}
   * @required
   * @property {string} eyebrow - Testo eyebrow
   * @property {string} title - Titolo principale
   * @property {string} text - Testo HTML
   * @property {string} image - URL immagine
   * @property {number|string} portrait - Flag layout (0 default, 1 portrait)
   * @property {Array} files - Documenti allegati
   * @property {Array} links - Link esterni
   */
  content: {
    type: Object,
    required: true
  }
})

// ──────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ──────────────────────────────────────────────────────────────

/**
 * Determina se layout è portrait
 * Accetta numero 0/1 o stringa "0"/"1" dal JSON
 * @returns {boolean} true se portrait mode
 */
const isPortrait = computed(() => Number(props.content?.portrait) === 1)

/**
 * Verifica se l'immagine è effettivamente disponibile
 * Controlla che sia una stringa non vuota
 * @returns {boolean} true se immagine valida
 */
const hasImage = computed(() => {
  return typeof props.content?.image === 'string' && props.content.image.trim().length > 0
})

// ──────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ──────────────────────────────────────────────────────────────

/**
 * Sanitizza HTML per evitare XSS
 * @param {string} html - HTML da sanitizzare
 * @returns {string} HTML sanitizzato
 */
function sanitizeHtml (html) {
  return sanitizeHtmlRegex(html)
}

/**
 * Icone fallback per file e link
 */
const pdfFallback  = '/images/logos/pdf_icon.png'
const linkFallback = '/images/logos/external_link.svg'
</script>

<style scoped lang="scss">
@import '@/assets/styles/partials/variables';
@import "@/assets/styles/partials/compat";
@import '@/assets/styles/partials/typography';

/* ──────────────────────────────────────────────────────────────
   SECTION BASE STYLES - Layout colonnare
   ────────────────────────────────────────────────────────────── */
.section-left-title {
  padding: $section-padding-y $space-1 $space-6 $space-1;

  .container {
    max-width: $container-max-width;
    margin: 0 auto;
  }

  .content {
    display: flex;
    flex-direction: column;
    @include flex-gap(clamp(2rem, 4vw, 3rem), column);
    align-items: flex-start;

    /* ──────────────────────────────────────────────────────────────
       LEFT COLUMN - Eyebrow e Titolo
    ────────────────────────────────────────────────────────────── */
    .left {
      .eyebrow {
        position: relative;
        display: inline-block;
        font-size: $font-eyebrow;
        font-weight: 600;
        text-transform: uppercase;
        color: $clr-tertiary;
        margin-bottom: $space-2;
        margin-left: 60px;
        cursor: default;
        white-space: nowrap;
        transition: all 0.3s ease;

        /* Barra sotto l'eyebrow */
        &::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: -100px;
          width: 40%;
          height: 12px;
          background-color: rgba($clr-tertiary, 0.7);
          transition: all 0.3s ease;
        }

        @media (hover: hover) {
          &:hover {
            color: $clr-tertiary;

            &::before {
              background: rgba($clr-tertiary, 0.8);
              width: 140%;
              transform: scaleY(1.2);
            }
          }
        }
      }

      .title {
        font-size: $font-title;
        font-weight: 300;
        margin: 0;
        color: $text-color;
        margin-left: 60px;
        margin-bottom: $space-2;
      }
    }

    /* ──────────────────────────────────────────────────────────────
       RIGHT COLUMN - Contenuto Principale
    ────────────────────────────────────────────────────────────── */
    .right {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding-left: clamp(4rem, 8vw, 8rem);

      /* Immagine hero - centrata orizzontalmente */
      .hero-image {
        width: 100%;
        margin: 0 auto $space-2 auto;
        text-align: center;
        
        img {
          width: 100%;
          max-width: 600px;
          height: auto;
          display: inline-block;
          box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
          transition: all 0.3s ease;

          @media (hover: hover) {
            &:hover {
              transform: scale(1.02);
              box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
            }
          }
        }
      }

      /* Layout portrait - immagine a destra ridotta + testo a sinistra */
      .content-with-portrait {
        display: flex;
        @include flex-gap($space-2);
        width: 100%;
        align-items: flex-start;

        .html-content {
          flex: 1;
          min-width: 0; /* evita overflow testo */
        }

        .portrait-image {
          flex: 0 0 auto;
          width: 300px;
          margin-left: $space-2;
          order: 2; /* sposta immagine a destra */

          img {
            width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
            transition: all 0.3s ease;

            @media (hover: hover) {
              &:hover {
                transform: scale(1.02);
                box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
              }
            }
          }
        }
      }

      /* Testo HTML con :deep per stilare elementi interni */
      .html-content {
        font-size: $font-base;
        line-height: 1.6;
        color: $text-color;
        width: 100%;

        :deep(p) {
          margin: 0 0 $space-1 0;
          font-size: inherit;
          line-height: inherit;
          color: inherit;

          &:last-child {
            margin-bottom: 0;
          }
        }

        :deep(strong) {
          font-weight: 700;
          color: $text-color;
        }

        :deep(em) {
          font-style: italic;
        }

        :deep(span) {
          font-size: inherit;
          line-height: inherit;
        }

        :deep(h4) {
          font-size: $font-h4;
          font-weight: 600;
          margin: $space-1 0 $space-0-5 0;
          color: $text-color;
        }

        :deep(ul) {
          margin: $space-0-5 0;
          padding-left: $space-1-5;
        }

        :deep(li) {
          margin-bottom: $space-0-5;
          font-size: inherit;
          line-height: inherit;
          color: inherit;
        }

        :deep(div) {
          margin: inherit;
        }

        /* Link styling - no underline, tertiary green color */
        :deep(a) {
          color: $clr-fonda;
          text-decoration: none;
        }
      }

      /* Allegati e link */
      .attachments {
        width: 100%;
        margin-top: $space-2;

        .attachments-title {
          font-size: $font-base;
          font-weight: 600;
          color: $text-color;
          margin: 0 0 $space-1 0;
        }

        .attachments-list {
          list-style: none;
          margin: 0;
          padding: 0;

          .attachment-row {
            margin-bottom: $space-0-75;

            a {
              display: inline-flex;
              align-items: center;
              @include flex-gap($space-1);
              color: $clr-tertiary;
              text-decoration: none;
              font-size: $font-base;
              transition: all 0.3s ease;

              @media (hover: hover) {
                &:hover {
                  color: $clr-secondary;
                  transform: translateX(4px);

                  .attachment-icon img {
                    transform: scale(1.1);
                  }
                }
              }

              .attachment-icon {
                flex-shrink: 0;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                  width: 100%;
                  height: 100%;
                  object-fit: contain;
                  transition: transform 0.3s ease;
                }
              }

              .attachment-label {
                font-weight: 600;
              }

              .file-size {
                font-size: $font-sm;
                color: $clr-text-light;
                margin-left: $space-0-5;
              }
            }
          }
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Desktop grande (1600px - 1999px)
   ────────────────────────────────────────────────────────────── */
@media (min-width: 1600px) and (max-width: 1999px) {
  .section-left-title {
    .content {
      @include flex-gap(clamp(2.5rem, 5vw, 4rem), column);

      .right {
        padding-left: clamp(5rem, 10vw, 10rem);
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Desktop (1301px - 1599px)
   ────────────────────────────────────────────────────────────── */
@media (min-width: 1301px) and (max-width: 1599px) {
  .section-left-title {
    .content {
      @include flex-gap(clamp(2rem, 4vw, 3.5rem), column);

      .right {
        padding-left: clamp(4.5rem, 9vw, 9rem);
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Desktop medio (1300px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1300px) {
  .section-left-title {
    padding: $space-6 $space-0-5 $space-5 $space-0-5;

    .content {
      @include flex-gap(clamp(1.5rem, 4vw, 2.5rem), column);

      .left {
        .eyebrow {
          margin-left: 50px;
          font-size: $font-eyebrow-lg;

          &::before {
            height: 10px;
            left: -80px;
            width: 40%;
          }

          @media (hover: hover) {
            &:hover::before {
              background: rgba($clr-tertiary, 0.8);
              width: 140%;
              transform: scaleY(1.2);
            }
          }
        }

        .title {
          font-size: $font-title-lg;
          margin-left: 50px;
        }
      }

      .right {
        padding-left: clamp(3rem, 6vw, 6rem);

        .hero-image {
          margin-bottom: $space-2;
        }

        /* Layout portrait responsive */
        .content-with-portrait {
          flex-direction: column;

          .portrait-image {
            width: 225px;
            margin: 0 auto $space-2 auto;
            order: 0;
          }
        }

        .html-content {
          font-size: $font-base-lg;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Tablet (1024px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .section-left-title {
    padding: $space-5 $space-0-5 $space-4 $space-0-5;

    .content {
      @include flex-gap(clamp(1.5rem, 3vw, 2rem), column);

      .left {
        .eyebrow {
          margin-left: 40px;

          &::before {
            left: -80px;
          }
        }

        .title {
          margin-left: 40px;
        }
      }

      .right {
        padding-left: clamp(2rem, 4vw, 4rem);
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Small Tablet (920px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 920px) {
  .section-left-title {
    .content {
      .left {
        .eyebrow {
          margin-left: 30px;
          margin-bottom: $space-0-5;
          font-size: $font-eyebrow-sm;

          &::before {
            height: 8px;
            left: -40px;
            width: 40%;
          }

          @media (hover: hover) {
            &:hover::before {
              background: rgba($clr-tertiary, 0.8);
              width: 140%;
              transform: scaleY(1.2);
            }
          }
        }

        .title {
          font-size: $font-title-sm;
          margin-left: 30px;
          margin-bottom: $space-0-5;
        }
      }

      .right {
        .html-content {
          font-size: $font-base-sm;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Mobile (768px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .section-left-title {
    padding: $space-4 $space-0-5 $space-3 $space-0-5;

    .content {
      @include flex-gap(clamp(1rem, 3vw, 1.5rem), column);

      .left {
        .eyebrow {
          margin-left: 30px;
          font-size: $font-eyebrow-sm;

          &::before {
            height: 8px;
            left: -40px;
            width: 40%;
          }

          @media (hover: hover) {
            &:hover::before {
              background: rgba($clr-tertiary, 0.8);
              width: 140%;
              transform: scaleY(1.2);
            }
          }
        }

        .title {
          margin-left: 30px;
          font-size: $font-title-lg;
        }
      }

      .right {
        padding-left: $space-1;

        .hero-image {
          margin-bottom: $space-1;

          img {
            max-width: 100%;
          }
        }

        .content-with-portrait {
          @include flex-gap($space-1);

          .portrait-image {
            width: 150px;
            margin: 0 0 $space-1 auto;
          }
        }

        .html-content {
          font-size: $font-base;
        }

        .attachments {
          margin-top: $space-1-5;

          .attachments-title {
            font-size: $font-sm;
          }

          .attachments-list {
            .attachment-row a {
              font-size: $font-sm;

              .attachment-icon {
                width: 20px;
                height: 20px;
              }
            }
          }
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Small Mobile (480px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .section-left-title {
    padding: $space-3 $space-0-5 $space-2 $space-0-5;

    .content {
      @include flex-gap($space-1-5, column);

      .left {
        .eyebrow {
          margin-left: 20px;
          margin-bottom: $space-0-25;
          font-size: $font-eyebrow-xs;

          &::before {
            height: 7px;
            left: -30px;
            width: 40%;
          }

          @media (hover: hover) {
            &:hover::before {
              background: rgba($clr-tertiary, 0.8);
              width: 140%;
              transform: scaleY(1.2);
            }
          }
        }

        .title {
          font-size: $font-title-xs;
          margin-left: 20px;
          margin-bottom: $space-0-5;
        }
      }

      .right {
        padding-left: $space-0-5;

        .html-content {
          font-size: $font-base-xs;
        }

        .content-with-portrait {
          @include flex-gap($space-0-75);

          .portrait-image {
            width: 120px;
            margin: 0 0 $space-1 auto;
          }
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Extra Small Mobile (360px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 360px) {
  .section-left-title {
    .content {
      .left {
        .eyebrow {
          margin-left: 15px;
          font-size: $font-eyebrow-xxs;

          &::before {
            height: 6px;
            left: -20px;
            width: 40%;
          }

          @media (hover: hover) {
            &:hover::before {
              background: rgba($clr-tertiary, 0.8);
              width: 140%;
              transform: scaleY(1.2);
            }
          }
        }

        .title {
          font-size: $font-title-xxs;
          margin-left: 15px;
        }
      }

      .right {
        .html-content {
          font-size: $font-base-xxs;
        }
      }
    }
  }
}
</style>